import React from 'react';
import clsx from 'clsx';
import * as BizCharts from 'bizcharts';
import {
	COMPARISON_SERIES_CURRENT,
	COMPARISON_SERIES_FIELD,
	COMPARISON_SERIES_PREVIOUS,
} from '../../utils/AnalyticsComparisonUtils';
import { emptyGeomLabel } from './emptyGeomLabel';

const { Chart, Axis, Geom, Legend, Tooltip } = BizCharts as any;

const AXIS_Y_LINE_COLOR = '#D9D9D9';
const AXIS_X_LINE_COLOR = '#E8E8E8';
const DEFAULT_HEIGHT = 360;
const DEFAULT_BAR_SIZE = 40;

export type SpendBarChartProps = {
	data: any[];
	xField?: string;
	yField?: string;
	hasComparison: boolean;
	currentColor: string;
	previousColor: string;
	axisXTitle?: string;
	axisYTitle: string;
	formatYAxisLabel: (val: any) => string;
	/** Tooltip definition for the interval geom in single mode (BizCharts `[fields, formatter]`). */
	intervalTooltipDefinition?: [string, (...args: any[]) => { name: string; value: string }];
	/** Custom content for the comparison-mode tooltip (Tooltip.customContent). */
	renderComparisonTooltip?: (title: string, items: any[]) => string;
	/** Click handler for bars in single (non-comparison) mode. */
	onIntervalClick?: (ev: any) => void;
	barSize?: number;
	comparisonBarSize?: number;
	height?: number;
	/**
	 * Restrict the category-axis range to a fraction of the plot width. Used to keep the
	 * intra-pair gap near a target pixel width when fewer pairs are displayed. Pass `undefined`
	 * to use the full width.
	 */
	monthLabelRange?: [number, number];
};

const SINGLE_ITEM_TPL = '<li data-index={index} class="m-0 list-none p-0">{value}</li>';

const ROTATED_LABEL = {
	autoRotate: false,
	rotate: -Math.PI / 4,
	offset: 18,
	style: {
		fontSize: 12,
		fill: 'rgba(0,0,0,0.45)',
		textAlign: 'end',
		textBaseline: 'middle',
	},
} as const;

const SpendBarChart = ({
	data,
	xField = 'monthLabel',
	yField = 'spendValue',
	hasComparison,
	currentColor,
	previousColor,
	axisXTitle = 'Month',
	axisYTitle,
	formatYAxisLabel,
	intervalTooltipDefinition,
	renderComparisonTooltip,
	onIntervalClick,
	barSize = DEFAULT_BAR_SIZE,
	comparisonBarSize,
	height = DEFAULT_HEIGHT,
	monthLabelRange,
}: SpendBarChartProps) => {
	const intervalSize = hasComparison ? comparisonBarSize || barSize : barSize;
	const seriesColor = (seriesValue: any) =>
		seriesValue === COMPARISON_SERIES_PREVIOUS ? previousColor : currentColor;

	const scale: any = {
		[yField]: {
			min: 0,
			nice: true,
		},
		[COMPARISON_SERIES_FIELD]: {
			type: 'cat',
			values: [COMPARISON_SERIES_PREVIOUS, COMPARISON_SERIES_CURRENT],
		},
		[xField]: monthLabelRange ? { type: 'cat', range: monthLabelRange } : { type: 'cat' },
	};

	const g2ChartScopeClass = hasComparison
		? 'analytics-g2-chart--comparison'
		: 'analytics-g2-chart--single';

	return (
		<div className={clsx('analytics-g2-chart w-full min-w-0', g2ChartScopeClass)}>
			<Chart
				height={height}
				data={data}
				autoFit={true}
				forceFit={true}
				theme="openwrench"
				scale={scale}
				onIntervalClick={hasComparison ? undefined : onIntervalClick}
			>
				<Axis
					name={xField}
					title={{
						text: axisXTitle,
						style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
						offset: 70,
					}}
					line={{ style: { stroke: AXIS_X_LINE_COLOR } }}
					tickLine={null}
					label={hasComparison ? null : ROTATED_LABEL}
					grid={{
						line: {
							style: {
								stroke: AXIS_Y_LINE_COLOR,
								lineDash: [4, 4],
							},
						},
					}}
				/>
				<Axis
					name={yField}
					title={{
						text: axisYTitle,
						style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
						offset: 80,
					}}
					label={{
						formatter: formatYAxisLabel,
						style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
					}}
					line={{ style: { stroke: AXIS_Y_LINE_COLOR } }}
					tickLine={null}
					grid={{
						line: {
							style: {
								stroke: AXIS_Y_LINE_COLOR,
								lineDash: [4, 4],
							},
						},
					}}
				/>
				<Legend visible={false} />
				{hasComparison ? (
					<Tooltip
						showTitle={false}
						shared={true}
						enterable={true}
						crosshairs={{ type: 'x' }}
						customContent={renderComparisonTooltip}
					/>
				) : (
					<Tooltip
						showTitle={false}
						enterable={true}
						crosshairs={{ type: 'y' }}
						itemTpl={SINGLE_ITEM_TPL}
					/>
				)}
				<Geom
					type="interval"
					size={intervalSize}
					position={`${xField}*${yField}`}
					color={hasComparison ? [COMPARISON_SERIES_FIELD, seriesColor] : currentColor}
					adjust={hasComparison ? [{ type: 'dodge', marginRatio: 0.2 }] : []}
					style={{ cursor: hasComparison ? 'default' : onIntervalClick ? 'pointer' : 'default' }}
					tooltip={intervalTooltipDefinition}
					label={
						hasComparison
							? [
									xField,
									{
										position: 'bottom',
										offset: -12,
										autoRotate: false,
										rotate: -Math.PI / 4,
										content: (rowData: any) =>
											rowData[COMPARISON_SERIES_FIELD] === COMPARISON_SERIES_PREVIOUS
												? rowData.previousMonthLabel
												: rowData[xField],
										style: {
											fontSize: 12,
											fill: 'rgba(0,0,0,0.45)',
											textAlign: 'end',
											textBaseline: 'middle',
										},
									},
							  ]
							: emptyGeomLabel(xField)
					}
				/>
			</Chart>
		</div>
	);
};

export default SpendBarChart;
